import { useState } from 'react';
import { Flower2, Star, ChevronDown, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface Review {
  id: string;
  userName: string;
  userInitials: string;
  planType: 'premium' | 'standard';
  date: string;
  rating: number;
  text: string;
  photos: string[];
  helpfulCount: number;
}

export const CustomerReviewsSection = () => {
  const { t } = useTranslation();
  const [reviewFilter, setReviewFilter] = useState<'all' | 'with-photos' | 'five-star' | 'premium' | 'standard'>('all');
  const [expandedPhotoIndex, setExpandedPhotoIndex] = useState<number | null>(null);
  const [reviews, setReviews] = useState<Review[]>([
    {
      id: '1',
      userName: 'Maya M.',
      userInitials: 'MM',
      planType: 'premium',
      date: '2 weeks ago',
      rating: 5,
      text: 'The flowers were absolutely stunning! They arrived perfectly fresh and the arrangement was beautiful. My living room has never looked better!',
      photos: ['maya-1.jpg', 'maya-2.jpg', 'maya-3.jpg'],
      helpfulCount: 12
    },
    {
      id: '2',
      userName: 'Sam T.',
      userInitials: 'ST',
      planType: 'standard',
      date: '1 month ago',
      rating: 4,
      text: 'Consistent quality every week. The flowers last at least 7 days and the delivery is always on time. Love the seasonal variety!',
      photos: ['sam-1.jpg'],
      helpfulCount: 8
    },
    // Add more reviews as needed
  ]);

  // Filter reviews based on selected filter
  const filteredReviews = (() => {
    switch(reviewFilter) {
      case 'five-star':
        return reviews.filter(review => review.rating === 5);
      case 'premium':
        return reviews.filter(review => review.planType === 'premium');
      case 'standard':
        return reviews.filter(review => review.planType === 'standard');
      default:
        return reviews;
    }
  })();

  // Toggle photo modal
  const togglePhotoModal = (photoIndex: number | null) => {
    setExpandedPhotoIndex(photoIndex);
  };

  // Mark review as helpful
  const markReviewAsHelpful = (reviewId: string) => {
    setReviews(reviews.map(review => 
      review.id === reviewId
        ? { ...review, helpfulCount: review.helpfulCount + 1 }
        : review
    ));
  };

  return (
    <>
      <section className="pt-4 py-4 shadow-sm my-8 bg-[#FFFFFF]" aria-labelledby="reviews-heading">
        <h2 id="reviews-heading" className="text-2xl font-bold mb-6 text-black text-center ">
          {t('components.customerReviews.title')}
        </h2>
        
        {/* Overall Rating Display */}
        <div className="flex flex-col items-center mb-6 px-5">
          <div className="bg-gradient-to-r from-amber-50 to-amber-100/50 py-4 px-6 rounded-xl w-full flex flex-col items-center">
            <div className="flex items-baseline">
              <span className="text-5xl font-bold text-amber-800">4.8</span>
              <span className="text-lg text-amber-700 ml-1">/5</span>
            </div>
            
            <div className="flex mt-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star 
                  key={star} 
                  fill="#F6A81C" 
                  className={`w-6 h-6 ${star <= 4.8 ? 'text-[#F6A81C]' : 'text-gray-300'}`} 
                />
              ))}
            </div>
            
            <p className="text-sm text-amber-700 mt-2 font-medium">
              {t('components.customerReviews.basedOnVerifiedDeliveries', { count: 128 })}
            </p>
          </div>
        </div>
        
        {/* Category Ratings */}
        <div className="px-5 mb-8">
          <div className="grid grid-cols-2 gap-4">
            {[
              { name: t('components.customerReviews.categories.freshness'), score: 4.9 },
              { name: t('components.customerReviews.categories.arrangement'), score: 4.7 },
              { name: t('components.customerReviews.categories.delivery'), score: 4.8 },
              { name: t('components.customerReviews.categories.value'), score: 4.6 }
            ].map((category) => (
              <div key={category.name} className="flex flex-col">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm text-gray-600">{category.name}</span>
                  <span className="text-sm font-medium text-gray-800">{category.score}</span>
                </div>
                <div className="h-3 bg-amber-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-amber-400 to-[#F6A81C]" 
                    style={{ width: `${(category.score / 5) * 100}%` }} 
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Photo Gallery */}
        <div className="mb-4">
          <div className="px-5 mb-2 flex justify-between items-center">
            <h3 className="font-medium text-amber-900">{t('components.customerReviews.customerPhotos')}</h3>
            <button className="text-sm text-amber-700 font-medium border-none bg-transparent p-0">
              {t('common.seeAll')}
            </button>
          </div>
          
          <div className="px-5 overflow-x-auto">
            <div className="flex gap-3 pb-2">
              {[1, 2, 3, 4, 5].map((photo) => (
                <div 
                  key={photo} 
                  className="w-28 h-28 flex-shrink-0 rounded-lg overflow-hidden shadow-md border border-amber-200 cursor-pointer transform hover:scale-105 transition-all hover:shadow-lg"
                >
                  <div className="w-full h-full bg-gradient-to-br from-amber-50 to-amber-100 flex items-center justify-center relative group">
                    <Flower2 className="w-10 h-10 text-amber-300 group-hover:opacity-0 transition-opacity" />
                    {/* Placeholder for real images */}
                    
                    {/* Overlay on hover */}
                    <div className="absolute inset-0 bg-amber-500/20 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                      <span className="bg-white/90 text-amber-800 px-2 py-1 rounded-md text-xs font-medium">
                        {t('components.customerReviews.viewLarger')}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Review Filters */}
        <div className="p-0 ms-4 mb-6">
          <h3 className="font-medium text-amber-900 mb-3">{t('components.customerReviews.filterReviews')}</h3>
          <div className="overflow-x-auto pb-1">
            <div className="flex gap-1">
              <button 
                onClick={() => setReviewFilter('all')} 
                className={`px-4 py-2 rounded-full text-sm whitespace-nowrap font-medium shadow-sm transition-all active:scale-95 transform ${
                  reviewFilter === 'all' 
                    ? 'bg-amber-500 text-white hover:bg-amber-600' 
                    : 'bg-white border-2 border-amber-200 text-amber-700 hover:bg-amber-50 hover:border-amber-300'
                }`}
              >
                {t('components.customerReviews.filters.allReviews')}
              </button>
              
              <button 
                onClick={() => setReviewFilter('five-star')} 
                className={`px-4 py-2 rounded-full text-sm whitespace-nowrap font-medium shadow-sm transition-all active:scale-95 transform flex items-center ${
                  reviewFilter === 'five-star' 
                    ? 'bg-amber-500 text-white hover:bg-amber-600' 
                    : 'bg-white border-2 border-amber-200 text-amber-700 hover:bg-amber-50 hover:border-amber-300'
                }`}
              >
                <span className="mr-1">5</span>
                <Star fill={reviewFilter === 'five-star' ? "white" : "#F6A81C"} className={`w-3 h-3 ${reviewFilter === 'five-star' ? 'text-white' : 'text-[#F6A81C]'}`} />
                <span className="ml-1">{t('components.customerReviews.filters.only')}</span>
              </button>
              
              <button 
                onClick={() => setReviewFilter('premium')} 
                className={`px-4 py-2 rounded-full text-sm whitespace-nowrap font-medium shadow-sm transition-all active:scale-95 transform ${
                  reviewFilter === 'premium' 
                    ? 'bg-amber-500 text-white hover:bg-amber-600' 
                    : 'bg-white border-2 border-amber-200 text-amber-700 hover:bg-amber-50 hover:border-amber-300'
                }`}
              >
                {t('components.customerReviews.filters.premium')}
              </button>
              
              <button 
                onClick={() => setReviewFilter('standard')} 
                className={`px-4 py-2 rounded-full text-sm whitespace-nowrap font-medium shadow-sm transition-all active:scale-95 transform ${
                  reviewFilter === 'standard' 
                    ? 'bg-amber-500 text-white hover:bg-amber-600' 
                    : 'bg-white border-2 border-amber-200 text-amber-700 hover:bg-amber-50 hover:border-amber-300'
                }`}
              >
                {t('components.customerReviews.filters.standard')}
              </button>
            </div>
          </div>
        </div>
        
        {/* Reviews List */}
        {filteredReviews.length > 0 ? (
          <div className="space-y-4 px-5">
            {filteredReviews.map((review) => (
              <div key={review.id} className="rounded-lg shadow-sm border border-amber-200 bg-white p-4">
                <div className="flex justify-between mb-1">
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-800 font-medium mr-3">
                      {review.userInitials}
                    </div>
                    <div>
                      <p className="font-medium">{review.userName}</p>
                      <div className="flex items-center">
                        <span className="bg-amber-100 text-amber-800 text-xs px-2 py-0.5 rounded-full mr-2">
                          {t(`components.customerReviews.plans.${review.planType}`)}
                        </span>
                        <span className="text-gray-500 text-xs">{review.date}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star 
                        key={star} 
                        fill={star <= review.rating ? "#F6A81C" : "#E5E7EB"} 
                        className={`w-4 h-4 ${star <= review.rating ? 'text-[#F6A81C]' : 'text-gray-200'}`} 
                      />
                    ))}
                  </div>
                </div>
                
                <p className="text-gray-700 mb-4">{review.text}</p>
                
                {review.photos.length > 0 && (
                  <div className="flex gap-2 mb-4 overflow-x-auto py-1">
                    {review.photos.map((photo, idx) => (
                      <div 
                        key={idx} 
                        className="w-20 h-20 flex-shrink-0 rounded-md overflow-hidden shadow-sm border border-amber-200 cursor-pointer"
                        onClick={() => togglePhotoModal(idx)}
                      >
                        <div className="w-full h-full bg-gradient-to-br from-amber-50 to-amber-100 flex items-center justify-center relative group">
                          <Flower2 className="w-6 h-6 text-amber-300 group-hover:opacity-0 transition-opacity" />
                          
                          <div className="absolute inset-0 bg-amber-500/20 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                            <span className="bg-white/90 text-amber-800 px-3 py-1 rounded-md text-sm font-medium">
                              {t('components.customerReviews.viewPhoto')}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="flex justify-between items-center">
                  <button 
                    onClick={() => markReviewAsHelpful(review.id)}
                    className="text-sm text-amber-800 hover:text-amber-600 transition-colors"
                  >
                    {t('components.customerReviews.markHelpful')} ({review.helpfulCount})
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="px-5 py-10 text-center">
            <p className="text-amber-700">{t('components.customerReviews.noMatchingReviews')}</p>
          </div>
        )}
        
        {/* View expanded photo modal */}
        {expandedPhotoIndex !== null && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75" onClick={() => togglePhotoModal(null)}>
            <div className="relative max-w-lg w-full p-2">
              <button 
                className="absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full p-2"
                onClick={(e) => {
                  e.stopPropagation();
                  togglePhotoModal(null);
                }}
              >
                <X className="w-6 h-6" />
              </button>
              
              <div className="bg-white rounded-lg overflow-hidden">
                <div className="p-4 border-b">
                  <h3 className="font-medium text-amber-900">{t('components.customerReviews.customerPhoto')}</h3>
                </div>
                
                <div className="aspect-square bg-gradient-to-br from-amber-50 to-amber-100 flex items-center justify-center">
                  <Flower2 className="w-20 h-20 text-amber-300" />
                </div>
              </div>
            </div>
          </div>
        )}
      </section>
    </>
  );
}; 